import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Phone, 
  FileText, 
  CheckCircle, 
  Award,
  Users,
  Clock,
  Shield,
  Heart,
  Target,
  Wrench,
  MapPin,
  Star
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: "Qualité",
      description: "Nous utilisons uniquement des équipements de qualité professionnelle des meilleures marques du marché."
    },
    {
      icon: Clock,
      title: "Réactivité",
      description: "Intervention rapide partout en France. Service d'urgence disponible 24h/24 et 7j/7."
    },
    {
      icon: Heart,
      title: "Proximité",
      description: "Entreprise basée à Moissy-Cramayel (77), nous intervenons partout en France selon les besoins."
    },
    {
      icon: Target,
      title: "Expertise",
      description: "13 ans d'expérience et une équipe de techniciens qualifiés et certifiés."
    },
    {
      icon: Wrench,
      title: "Service complet",
      description: "De l'étude à la maintenance, nous vous accompagnons à chaque étape de votre projet."
    },
    {
      icon: Users,
      title: "Satisfaction client",
      description: "Plus de 2000 clients nous font confiance. Votre satisfaction est notre priorité."
    }
  ];

  const milestones = [
    { year: "2012", event: "Début de l'activité dans le domaine de la climatisation" },
    { year: "2015", event: "Extension vers les pompes à chaleur et le froid commercial" },
    { year: "2018", event: "Plus de 1000 installations réalisées" },
    { year: "2020", event: "Renforcement du service d'urgence 24h/24" },
    { year: "2025", event: "Création officielle d'ADM Confort Clim" }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-[#F8FAFC] py-20" data-testid="about-hero">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="badge-primary mb-4">À propos</span>
              <h1 className="font-outfit text-4xl md:text-5xl font-bold text-[#000000] mt-4 mb-6">
                13 ans d'expertise au service de votre confort
              </h1>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                ADM Confort Clim est une entreprise spécialisée dans la climatisation, les pompes à chaleur, 
                le froid commercial et la ventilation. Basés à Moissy-Cramayel (Seine-et-Marne), nous intervenons 
                en Île-de-France et dans toute la France selon vos besoins.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                Notre équipe de techniciens qualifiés met son savoir-faire et son expérience à votre 
                disposition pour vous garantir des installations de qualité et un service irréprochable.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/contact"
                  className="btn-primary flex items-center justify-center gap-2"
                  data-testid="about-cta-contact"
                >
                  <FileText size={18} />
                  <span>Demander un devis</span>
                </Link>
                <a 
                  href="tel:0603054911"
                  className="btn-secondary flex items-center justify-center gap-2"
                  data-testid="about-cta-call"
                >
                  <Phone size={18} />
                  <span>06 03 05 49 11</span>
                </a>
              </div>
            </motion.div>
            
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/mqux4oem_WhatsApp%20Image%202026-03-15%20at%2022.05.57.jpeg"
                alt="Technicien ADM Confort Clim - Diagnostic climatisation confort thermique"
                className="w-full h-[500px] object-cover rounded-2xl shadow-lg"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-[#1997b0] rounded-full flex items-center justify-center">
                    <Award className="text-white" size={24} />
                  </div>
                  <div>
                    <div className="font-outfit text-2xl font-bold text-[#000000]">13 ans</div>
                    <div className="text-sm text-slate-600">d'expérience</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#1997b0]" data-testid="about-stats">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: "13", label: "Années d'expérience" },
              { number: "2000+", label: "Clients satisfaits" },
              { number: "24/7", label: "Service d'urgence" },
              { number: "100%", label: "Satisfaction garantie" }
            ].map((stat, index) => (
              <motion.div 
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-4xl md:text-5xl font-bold text-white font-outfit">{stat.number}</div>
                <div className="text-sm text-white/80 mt-2">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-white" data-testid="about-values">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            {...fadeInUp}
          >
            <span className="badge-primary mb-4">Nos valeurs</span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-[#000000] mt-4 mb-4">
              Ce qui nous différencie
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Chez ADM Confort Clim, nous mettons un point d'honneur à respecter des valeurs 
              qui font notre force et notre réputation.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="card-service"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`about-value-${index}`}
              >
                <div className="service-icon mb-6">
                  <value.icon className="text-[#1997b0]" size={28} />
                </div>
                <h3 className="font-outfit text-xl font-semibold text-[#000000] mb-3">{value.title}</h3>
                <p className="text-slate-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* History Section */}
      <section className="section-padding bg-[#F8FAFC]" data-testid="about-history">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeInUp}>
              <span className="badge-primary mb-4">Notre histoire</span>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold text-[#000000] mt-4 mb-6">
                Une entreprise à taille humaine
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Fondée sur une passion pour le métier et un sens aigu du service client, 
                ADM Confort Clim s'est construite au fil des années grâce à la confiance 
                de ses clients et à la qualité de ses prestations.
              </p>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Aujourd'hui, forte de 13 ans d'expérience, notre entreprise continue de se 
                développer tout en conservant ses valeurs fondamentales : proximité, réactivité 
                et excellence du service.
              </p>
              <div className="space-y-4">
                {milestones.map((milestone, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-16 h-8 bg-[#1997b0] rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-semibold">{milestone.year}</span>
                    </div>
                    <p className="text-slate-600 pt-1">{milestone.event}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/mqux4oem_WhatsApp%20Image%202026-03-15%20at%2022.05.57.jpeg"
                alt="Technicien ADM Confort Clim - Expertise climatisation"
                className="w-full h-[350px] object-cover rounded-2xl shadow-lg"
              />
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-2xl shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="text-[#1997b0]" size={18} />
                    <span className="font-semibold text-[#000000]">Notre siège</span>
                  </div>
                  <p className="text-slate-600 text-sm">
                    209 rue de Changis<br />
                    77550 Moissy-Cramayel
                  </p>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="text-[#fabc63]" size={18} />
                    <span className="font-semibold text-[#000000]">Notre engagement</span>
                  </div>
                  <p className="text-slate-600 text-sm">
                    Qualité et satisfaction<br />
                    garanties
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="section-padding bg-white" data-testid="about-certifications">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-12"
            {...fadeInUp}
          >
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-[#000000] mb-4">
              Nos engagements qualité
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Nous travaillons avec les plus grandes marques et respectons les normes les plus strictes.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: "Garantie décennale",
                description: "Toutes nos installations sont couvertes par notre garantie décennale pour votre tranquillité."
              },
              {
                icon: CheckCircle,
                title: "Techniciens certifiés",
                description: "Nos techniciens sont formés et certifiés pour intervenir sur tous types d'équipements."
              },
              {
                icon: Award,
                title: "Partenaire des grandes marques",
                description: "Daikin, Mitsubishi, Atlantic, Toshiba... Nous installons les meilleures marques du marché."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-[#F8FAFC] p-8 rounded-2xl text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-16 h-16 bg-[#1997b0]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <item.icon className="text-[#1997b0]" size={28} />
                </div>
                <h3 className="font-outfit text-xl font-semibold text-[#000000] mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1997b0]" data-testid="about-cta-section">
        <div className="container-custom">
          <motion.div 
            className="text-center text-white"
            {...fadeInUp}
          >
            <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-4">
              Faites confiance à des experts
            </h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              Contactez-nous pour discuter de votre projet ou obtenir un devis gratuit et personnalisé.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="tel:0603054911"
                className="btn-emergency flex items-center gap-2"
                data-testid="about-final-cta-call"
              >
                <Phone size={20} />
                <span>06 03 05 49 11</span>
              </a>
              <Link 
                to="/contact"
                className="bg-white text-[#1997b0] hover:bg-slate-100 transition-all duration-300 rounded-full px-8 py-4 font-semibold flex items-center gap-2"
                data-testid="about-final-cta-contact"
              >
                <FileText size={20} />
                <span>Demander un devis gratuit</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
